import { useCmsQueryParamsState } from '@hyphengroup/cms-components/hooks/useQueryParamsState';
import { isNotUndefined } from '@hyphengroup/cms-components/utils/common';
import { TravelCompareQuotesParams } from '@hyphengroup/cms-components/utils/searchParams/travelSearchParams';
import type { TiSearchParamKey } from '@hyphengroup/cms-components/utils/travelInsurance/tiFunnel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSafeSearchParamsExtended } from '../../../../../../hooks/useSafeSearchParams/useSafeSearchParams';
import { usePageDataContext } from '../../../../../app/[site]/[locale]/(cms)/_modules/cmsService/pageDataService/pageDataProvider';
import {
  CmsCountry,
  ComponentTravelPolicySearch,
} from '../../../../../app/[site]/[locale]/(cms)/_modules/cmsService/types';
import { travelCompareQuotesParamsHash } from '../../../../../app/[site]/[locale]/(cms)/_modules/travelQuotesService/helpers';
import { normalizeTravelParams } from '$/(cms)/_modules/travelQuotesService/normalizeTravelParams';
import type { StateSchema } from '$/components/cms/helpers/types';
import { useClientContext } from '$/modules/context/client';

export type TravelSearchQueryRaw = Omit<
  TravelCompareQuotesParams,
  'destinationIsoCodes' | 'insuranceType'
> & {
  destinations?: string[];
  tripType: string;
};

// `satisfies` couples this schema to TRAVEL_SEARCH_URL_PARAM_KEYS in
// cms-components (CMS-1866): every param written here is stripped by
// `useAppendQueryParams` on links leaving the TI purchase funnel. Adding a
// param without registering it there fails this build instead of silently
// reopening the leak.
export const TRAVEL_SEARCH_STATE_SCHEMA: StateSchema<TravelSearchQueryRaw> = {
  destinations: 'string[]',
  tripType: 'string',
  region: 'string',
  startDate: 'string',
  endDate: 'string',
  noOfAdults: 'number',
  noOfChildren: 'number',
  travelAsFamily: 'boolean',
} satisfies StateSchema<Record<TiSearchParamKey, unknown>>;

export const useTravelSearchQuery = (
  componentProps: ComponentTravelPolicySearch,
) => {
  const {
    pageData: { travelQuotes },
  } = usePageDataContext();
  const { brand } = useClientContext();

  const [rawParams, setRawParams] =
    useCmsQueryParamsState<TravelSearchQueryRaw>(TRAVEL_SEARCH_STATE_SCHEMA, {
      skipCmsPrefix: true,
    });

  // DEFAULT default params should be set to url, if not present
  const [initialUrlIsSet, setInitialUrlIsSet] = useState(false);
  const safeSearchParamsContext = useSafeSearchParamsExtended();
  useEffect(() => {
    if (safeSearchParamsContext?.searchParamsReady && !initialUrlIsSet) {
      let mergedParams = {
        ...travelQuotes?.params,
        ...rawParamsToTravelParams(rawParams, travelQuotes?.countries || []),
      };

      mergedParams = normalizeTravelParams(mergedParams, componentProps, brand);

      setRawParams(
        travelParamsToRawParams(mergedParams, travelQuotes?.countries || []),
      );

      // we have a delay between setting url and receiving updated searchParams from context
      const mergedHash = travelCompareQuotesParamsHash(mergedParams);
      const currentHash = travelCompareQuotesParamsHash(
        rawParamsToTravelParams(rawParams, travelQuotes?.countries || []),
      );

      if (mergedHash === currentHash) {
        setInitialUrlIsSet(true);
      }
    }
  }, [
    initialUrlIsSet,
    rawParams,
    safeSearchParamsContext?.searchParamsReady,
    setRawParams,
    travelQuotes,
    componentProps,
    brand,
  ]);
  // END DEFAULT

  const travelQueryParams = useMemo<TravelCompareQuotesParams>(() => {
    if (!initialUrlIsSet) {
      return normalizeTravelParams(
        travelQuotes?.params ?? {},
        componentProps,
        brand,
      );
    }
    return rawParamsToTravelParams(rawParams, travelQuotes?.countries || []);
  }, [
    initialUrlIsSet,
    rawParams,
    travelQuotes?.params,
    travelQuotes?.countries,
    componentProps,
    brand,
  ]);

  const setTravelQueryParams = useCallback(
    (value: TravelCompareQuotesParams) => {
      const normalized = normalizeTravelParams(value, componentProps, brand);
      setRawParams(
        travelParamsToRawParams(normalized, travelQuotes?.countries || []),
      );
    },
    [setRawParams, travelQuotes, componentProps, brand],
  );

  return {
    travelQueryParams,
    setTravelQueryParams,
    queryParamsReady: initialUrlIsSet,
  };
};

/**
 * The URL's trip parameters as the quote service takes them. `destinations` are country labels in
 * the URL and ISO codes in a quote, so the conversion needs the country list. Every reader of the
 * travel search URL resolves it here, so they all price the trip the results page did.
 */
export function rawParamsToTravelParams(
  props: TravelSearchQueryRaw,
  countries: CmsCountry[],
): TravelCompareQuotesParams {
  // do not use spread to avoid extra params
  return {
    endDate: props.endDate,
    noOfAdults: props.noOfAdults,
    noOfChildren: props.noOfChildren,
    region: props.region,
    startDate: props.startDate,
    travelAsFamily: props.travelAsFamily,
    destinationIsoCodes: props.destinations
      ?.map((dest) => countries.find((c) => c.value === dest)?.iso)
      .filter(isNotUndefined),
    insuranceType: props.tripType as TravelCompareQuotesParams['insuranceType'],
  };
}

function travelParamsToRawParams(
  props: TravelCompareQuotesParams,
  countries: CmsCountry[],
): TravelSearchQueryRaw {
  // do not use spread to avoid extra params
  return {
    endDate: props.endDate,
    noOfAdults: props.noOfAdults,
    noOfChildren: props.noOfChildren,
    region: props.region,
    startDate: props.startDate,
    travelAsFamily: props.travelAsFamily,
    destinations: props.destinationIsoCodes
      ?.map((iso) => countries.find((c) => c.iso === iso)?.value)
      .filter(isNotUndefined),
    tripType: props.insuranceType,
  };
}
